import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getUserTenantId } from "@talentech/bff-login";
import {
  ActionButton,
  ActionsMenu,
  AddonTextfield,
  Alert,
  Box,
  Button,
  ButtonColors,
  Grid,
  Icon,
  LightTable,
  Modal,
  PageTitle,
  Row,
  Spinner,
  TalentechActionButtonVariant,
  TalentechColors,
  TalentechIcons,
  Typography,
} from "@talentech/components";
import ToggleButtonGroup from "@/components/ToggleButtonGroup/ToggleButtonGroup";
import {
  useCreateJobMutation,
  useDeleteJobMutation,
  useGetJobsQuery,
  useLazyGetJobQuery,
  useUnpublishJobMutation,
} from "@/redux/reducer/directJobs.api";
import {
  DirectJobForm,
  JobListItem,
  JobSource,
  JobStatus,
} from "@/redux/reducer/directJobs.types";
import { NEW_JOB_ID, jobPath } from "./jobRoutes";
import JobPreviewModal from "./JobPreviewModal";
import JobStatusBadge from "./JobStatusBadge";

const SEARCH_DELAY_MS = 300;

const ALL_STATUSES = "all";

type StatusFilter = JobStatus | typeof ALL_STATUSES;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: ALL_STATUSES, label: "All" },
  { value: JobStatus.PUBLISHED, label: "Published" },
  { value: JobStatus.UNPUBLISHED, label: "Unpublished" },
];

const SOURCE_LABELS: Record<JobSource, string> = {
  [JobSource.ATS]: "ATS",
  [JobSource.DIRECT]: "Direct Job",
};

const openPublicUrl = (job: JobListItem) => {
  if (job.public_url) {
    window.open(job.public_url, "_blank");
  }
};

const JobsList: React.FC = () => {
  const navigate = useNavigate();
  const tenantId = useSelector(getUserTenantId);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(ALL_STATUSES);
  const [jobToDelete, setJobToDelete] = useState<JobListItem | null>(null);
  const [jobToUnpublish, setJobToUnpublish] = useState<JobListItem | null>(
    null
  );
  const [jobToClone, setJobToClone] = useState<JobListItem | null>(null);
  const [cloneTitle, setCloneTitle] = useState("");
  const [isCloning, setIsCloning] = useState(false);
  const [previewForm, setPreviewForm] = useState<DirectJobForm | null>(null);
  const [deleteJob, { isLoading: isDeleting }] = useDeleteJobMutation();
  const [getJob] = useLazyGetJobQuery();
  const [createJob] = useCreateJobMutation();
  const [unpublishJob, { isLoading: isUnpublishing }] =
    useUnpublishJobMutation();
  const {
    data: allJobs,
    isLoading,
    isError: isListError,
  } = useGetJobsQuery({ tenantId });
  const { currentData: jobs, isError: isFilterError } = useGetJobsQuery({
    tenantId,
    ...(search && { search }),
    ...(statusFilter !== ALL_STATUSES && { status: statusFilter }),
  });

  useEffect(() => {
    const timeout = setTimeout(
      () => setSearch(searchInput.trim()),
      SEARCH_DELAY_MS
    );
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const openNewJob = () =>
    navigate(jobPath(NEW_JOB_ID), { viewTransition: true });

  const editJob = (job: JobListItem) =>
    navigate(jobPath(job.job_id), { viewTransition: true });

  const openPreview = (job: JobListItem) =>
    getJob({ tenantId, jobId: job.job_id })
      .unwrap()
      .then(({ form }) => setPreviewForm(form))
      .catch(() => toast.error("Error loading the job"));

  const jobActions = (job: JobListItem) => {
    const open = {
      label: "Open",
      onClick: () => openPublicUrl(job),
      icon: <i className="fa-sharp fa-light fa-globe"></i>,
    };
    if (job.source !== JobSource.DIRECT) {
      return job.public_url ? [open] : [];
    }
    return [
      {
        label: "Edit",
        onClick: () => editJob(job),
        icon: <i className="fa-sharp fa-light fa-pencil"></i>,
      },
      { divider: true },
      ...(job.public_url ? [open] : []),
      {
        label: "Open Preview",
        onClick: () => openPreview(job),
        icon: (
          <i className="fa-sharp fa-light fa-magnifying-glass-waveform"></i>
        ),
      },
      { divider: true },
      {
        label: "Clone",
        onClick: () => {
          setJobToClone(job);
          setCloneTitle(`Copy of ${job.title}`);
        },
        icon: <i className="fa-sharp fa-light fa-clone"></i>,
      },
      { divider: true },
      ...(job.status === JobStatus.PUBLISHED
        ? [
            {
              label: "Unpublish",
              onClick: () => setJobToUnpublish(job),
              className: "text-danger",
              icon: <i className="fa-sharp fa-light fa-clock-rotate-left"></i>,
            },
          ]
        : []),
      {
        label: "Delete Job",
        onClick: () => setJobToDelete(job),
        className: "text-danger",
        icon: <i className="fa-sharp fa-light fa-trash"></i>,
      },
    ];
  };

  const onCloneConfirm = () => {
    if (!jobToClone || !cloneTitle.trim()) {
      return;
    }
    setIsCloning(true);
    const clone = getJob({ tenantId, jobId: jobToClone.job_id })
      .unwrap()
      .then(({ form }) =>
        createJob({
          tenantId,
          form: {
            ...form,
            title: cloneTitle.trim(),
            show_from: null,
            show_until: null,
          },
        }).unwrap()
      );
    toast
      .promise(clone, {
        pending: "Cloning...",
        success: "Job cloned",
        error: "Error cloning the job",
      })
      .then(() => setJobToClone(null))
      .catch(() => undefined)
      .finally(() => setIsCloning(false));
  };

  const onDeleteConfirm = () => {
    if (!jobToDelete) {
      return;
    }
    toast
      .promise(deleteJob({ tenantId, jobId: jobToDelete.job_id }).unwrap(), {
        pending: "Deleting...",
        success: "Job deleted",
        error: "Error deleting the job",
      })
      .then(() => setJobToDelete(null))
      .catch(() => undefined);
  };

  const onUnpublishConfirm = () => {
    if (!jobToUnpublish) {
      return;
    }
    toast
      .promise(
        unpublishJob({ tenantId, jobId: jobToUnpublish.job_id }).unwrap(),
        {
          pending: "Unpublishing...",
          success: "Job unpublished",
          error: "Error unpublishing the job",
        }
      )
      .then(() => setJobToUnpublish(null))
      .catch(() => undefined);
  };

  return (
    <>
      <PageTitle
        title="Jobs"
        additionalContent={
          <ActionButton
            color={ButtonColors.PRIMARY}
            label="Add New"
            variant={TalentechActionButtonVariant.VERTICAL}
            icon={TalentechIcons.BOLD_01_INTERFACE_ESSENTIALS_ALERTS_PLUS}
            onClick={openNewJob}
          />
        }
      />
      <Grid container className="mt-4">
        <Row>
          <Grid xs={12}>
            <Box paper rounded spacing={3}>
              {isLoading ? (
                <Spinner />
              ) : (isListError && !allJobs) || (isFilterError && !jobs) ? (
                <Alert
                  color={TalentechColors.DANGER}
                  message="Jobs could not be loaded. Reload the page to try again."
                />
              ) : allJobs?.length === 0 ? (
                <Box className="text-center py-5">
                  <Typography variant="h2">Create your first job</Typography>
                  <Typography variant="p" className="text-muted">
                    Jobs you create here are published to your career sites.
                  </Typography>
                  <Button
                    label="Add New"
                    color={ButtonColors.PRIMARY}
                    icon={<i className="fa-sharp fa-light fa-plus me-2" />}
                    onClick={openNewJob}
                  />
                </Box>
              ) : (
                <>
                  <Box flex className="gap-3 align-items-center mb-3">
                    <div className="flex-grow-1">
                      <AddonTextfield
                        addonContent={
                          <Icon
                            icon={
                              TalentechIcons.LIGHT_01_INTERFACE_ESSENTIALS_SEARCH_SEARCH
                            }
                          />
                        }
                        textfieldProps={{
                          type: "search",
                          placeholder: "Search by job title",
                          "aria-label": "Search by job title",
                          value: searchInput,
                          onChange: (
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => setSearchInput(event.target.value),
                        }}
                      />
                    </div>
                    <ToggleButtonGroup
                      ariaLabel="Filter by status"
                      options={STATUS_FILTERS}
                      value={statusFilter}
                      onChange={setStatusFilter}
                    />
                  </Box>
                  <LightTable
                    data={jobs ?? []}
                    emptyState={
                      jobs ? (
                        <div className="text-center p-4">
                          No jobs match your search
                        </div>
                      ) : (
                        <Spinner />
                      )
                    }
                    columns={[
                      {
                        label: "Job title",
                        accessor: "title",
                        Component: (job: JobListItem) => <div>{job.title}</div>,
                      },
                      {
                        label: "Source",
                        accessor: "source",
                        Component: (job: JobListItem) => (
                          <div>{SOURCE_LABELS[job.source]}</div>
                        ),
                      },
                      {
                        label: "Status",
                        accessor: "status",
                        Component: (job: JobListItem) => (
                          <JobStatusBadge status={job.status} />
                        ),
                      },
                      {
                        label: "Actions",
                        accessor: "job_id",
                        Component: (job: JobListItem) => {
                          const actions = jobActions(job);
                          return (
                            actions.length > 0 && (
                              // @ts-ignore
                              <ActionsMenu actions={actions} />
                            )
                          );
                        },
                      },
                    ]}
                  />
                </>
              )}
            </Box>
          </Grid>
        </Row>
      </Grid>

      <Modal
        showModal={!!jobToDelete}
        onClose={() => setJobToDelete(null)}
        title="Delete job"
        buttons={[
          <Button
            key="close"
            label="close"
            onClick={() => setJobToDelete(null)}
            outline
            color={ButtonColors.PRIMARY}
          />,
          <Button
            key="delete"
            isLoading={isDeleting}
            icon={
              <Icon
                icon={TalentechIcons.REGULAR_01_INTERFACE_ESSENTIALS_DELETE_BIN}
              />
            }
            color={ButtonColors.DANGER}
            label="delete"
            onClick={onDeleteConfirm}
          />,
        ]}
      >
        <div>
          Are you sure you want to delete this job:{" "}
          <strong>{jobToDelete?.title}</strong>?
        </div>
      </Modal>

      <Modal
        showModal={!!jobToUnpublish}
        onClose={() => setJobToUnpublish(null)}
        title="Unpublish job"
        buttons={[
          <Button
            key="close"
            label="close"
            onClick={() => setJobToUnpublish(null)}
            outline
            color={ButtonColors.PRIMARY}
          />,
          <Button
            key="unpublish"
            isLoading={isUnpublishing}
            icon={<i className="fa-sharp fa-light fa-clock-rotate-left me-2" />}
            color={ButtonColors.DANGER}
            label="unpublish"
            onClick={onUnpublishConfirm}
          />,
        ]}
      >
        <div>
          Are you sure you want to unpublish this job:{" "}
          <strong>{jobToUnpublish?.title}</strong>?
        </div>
      </Modal>

      <Modal
        showModal={!!jobToClone}
        onClose={() => setJobToClone(null)}
        title="Clone job"
        buttons={[
          <Button
            key="cancel"
            label="Cancel"
            onClick={() => setJobToClone(null)}
            outline
            color={ButtonColors.PRIMARY}
          />,
          <Button
            key="clone"
            isLoading={isCloning}
            icon={
              <Icon
                icon={
                  TalentechIcons.REGULAR_01_INTERFACE_ESSENTIALS_COPY_PASTE_DUPLICATE
                }
              />
            }
            color={ButtonColors.PRIMARY}
            label="Clone"
            onClick={onCloneConfirm}
          />,
        ]}
      >
        <div className="mb-3">
          You are cloning job: <strong>{jobToClone?.title}</strong>
        </div>
        <div className="mb-3">
          <label htmlFor="clone-job-title" className="form-label">
            New job title:
          </label>
          <input
            type="text"
            className="form-control"
            id="clone-job-title"
            value={cloneTitle}
            onChange={(event) => setCloneTitle(event.target.value)}
          />
        </div>
      </Modal>

      <JobPreviewModal
        tenantId={tenantId}
        form={previewForm}
        onClose={() => setPreviewForm(null)}
      />
    </>
  );
};

export default JobsList;
